import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {DateRangeSelectorComponent} from "../../../layout/date-range-selector/date-range-selector.component";
import {DropdownSelectComponent} from "../../../layout/dropdown-select/dropdown-select.component";
import {DropdownSelectOptionComponent} from "../../../layout/dropdown-select-option/dropdown-select-option.component";
import {FormsModule} from "@angular/forms";
import { NgClass, NgOptimizedImage } from "@angular/common";
import {TranslatePipe} from "@ngx-translate/core";
import {forkJoin} from 'rxjs';
import {Product} from '../../../../models/product';
import {Offer} from '../../../../models/offer';
import {User} from '../../../../models/user';
import {Partner} from '../../../../models/partner/partner';
import {Country} from '../../../../models/country';
import {LandingPage} from '../../../../models/landing-page/landing-page';
import {AdvertisingPlatform} from '../../../../models/advertising-platform';
import {ProductService} from '../../../../services/product/product.service';
import {OfferService} from '../../../../services/offer/offer.service';
import {UserService} from '../../../../services/user/user.service';
import {PartnerService} from '../../../../services/partner/partner.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {LandingPageService} from '../../../../services/landing-page/landing-page.service';
import {StatisticsService} from '../../../../services/statistics/statistics.service';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {AdvertisingPlatformService} from '../../../../services/advertising-platform/advertising-platform.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import {CountryService} from '../../../../services/country/country.service';
import {LangService} from '../../../../services/language/lang.service';
import {ElementLoaderDirective} from '../../../../directives/element-loader/element-loader.directive';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {MetaTitle} from '../../../../decorator/meta-title';
import {PercentPipe} from '../../../../pipes/percent/percent.pipe';
import {Language} from '../../../../models/language';
import {FileDownloadService} from '../../../../services/file-download/file-download.service';
import {UserActivityService} from '../../../../services/user-activity/user-activity.service';
import {LandingPageActivityPeriods} from '../../../../constants/landing-page-activity';
import {PoolService} from '../../../../services/landing-page/pool.service';
import {Pool} from '../../../../models/landing-page/pool';

@Component({
  selector: 'app-roi',
  imports: [
    DateRangeSelectorComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    FormsModule,
    NgOptimizedImage,
    TranslatePipe,
    ElementLoaderDirective,
    NgClass,
    MoneyPipe
],
  providers: [
    MoneyPipe,
    PercentPipe,
  ],
  templateUrl: './roi.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './roi.component.scss'
})
@MetaTitle('ROI statistics')
export class RoiComponent implements OnInit {
  products: Product[] = [];
  offers: Offer[] = [];
  accountManagers: User[] = [];
  partners: Partner[] = [];
  countries: Country[] = [];
  languages: Language[] = [];
  landingPages: LandingPage[] = [];
  pools: Pool[] = [];
  rows: any[] = [];
  totals: any = {};
  timeDays: number[] = [];
  advertisingPlatformList: AdvertisingPlatform[] = [];
  maxArpu: number = 0;
  maxSaleCount: number = 0;

  loading = true;
  offerLoading = false;
  partnerLoading = false;
  landingPageLoading = false;
  activityPeriods = LandingPageActivityPeriods;

  partnerAccountTypeList: string[] = [];

  filters: any = {
    product: null,
    offer: null,
    accountType: null,
    advertisingPlatform: null,
    accountManager: null,
    partner: null,
    country: null,
    lang: null,
    landingPage: null,
    landingPageActivity: null,
    pool: null,
    groupBy: 'date-day',
    subid: '',
    tc1: '',
    tc2: '',
    tc3: '',
    tc4: '',
    source: '',
    coreg: null,
    cross: null,
    goalTypeNames: [],
    timeCutout: 10,
    dataType: 'roi'
  };

  currentFilters: any = {};

  moreDetails: any = {
    lead: false,
    firstProposal: false,
    strategy: false,
    doubleAnim: false,
    auto: false,
    loyalty: false,
    opar: false,
    misc: false,
    total: false,
  };

  goalTypeNameList = [
    {key: 'first', label: 'First proposal'},
    {key: 'strategy', label: 'Strat'},
    {key: 'doubleanim', label: 'Double anim'},
    {key: 'auto', label: 'Fid auto'},
    {key: 'loyalty', label: 'Fid manuelle'},
    {key: 'opar', label: 'Opar'},
    {key: 'misc', label: 'Misc'},
  ];

  timeCutoutList = [
    {key: 1, label: '1'},
    {key: 2, label: '2'},
    {key: 5, label: '5'},
    {key: 10, label: '10'},
    {key: 15, label: '15'},
    {key: 20, label: '20'},
    {key: 30, label: '30'},
    {key: 60, label: '60'},
    {key: 90, label: '90'},
    {key: 180, label: '180'},
    {key: 360, label: '360'},
  ];

  dataTypeList = [
    {key: 'revenue', label: 'Revenue (ex. TAX)'},
    {key: 'arpu', label: 'ARPU (ex. TAX)'},
    {key: 'roi', label: 'ROI (ex. TAX)'},
    // {key: 'conversionRate', label: 'Conversion rate'},
    // {key: 'customerCount', label: 'Customer count'},
    {key: 'saleCount', label: 'Sale count'},
  ];

  @ViewChild(DateRangeSelectorComponent) dateSelector!: DateRangeSelectorComponent;

  constructor(
    private fileDownload: FileDownloadService,
    private userActivity: UserActivityService,
    private productService: ProductService,
    private offerService: OfferService,
    private userService: UserService,
    private partnerService: PartnerService,
    private auth: AuthService,
    private landingPageService: LandingPageService,
    private statisticsService: StatisticsService,
    private dialog: DialogService,
    private advertisingPlatformService: AdvertisingPlatformService,
    private moneyPipe: MoneyPipe,
    private percentPipe: PercentPipe,
    private poolService: PoolService,
    protected loader: LoaderService,
    protected countryService: CountryService,
    protected langService: LangService,
  ) {
  }

  ngOnInit(): void {
    this.goalTypeNameList.forEach(goalType => {
      this.filters.goalTypeNames.push(goalType.key);
    });

    this.loader.startLoading();

    forkJoin([
      this.productService.findAll({sort: 'name'}),
      this.userService.findAll({accountManager: true, sort: 'firstname,lastname'}),
      this.countryService.findAll(),
      this.auth.getAsyncConfig(),
      this.advertisingPlatformService.getList(),
      this.langService.getLanguages(),
      this.poolService.findAll({sort: 'name'}),
    ]).subscribe(([products, accountManagers, countries, config, advertisingPlatforms, languages, pools]) => {
      this.products = products;
      this.accountManagers = accountManagers;
      this.countries = countries;
      this.advertisingPlatformList = advertisingPlatforms;
      this.languages = languages;
      this.pools = pools;

      this.partnerAccountTypeList = config?.data?.partnerAccountTypeList;

      this.refreshOffers();
      this.refreshPartners();
      this.refreshLandingPages();

      // this.dateSelector.dateSelector = 'custom';
      // this.dateSelector.dateFromSelector = '2025-04-20';
      // this.dateSelector.dateToSelector = '2025-04-30';
      // this.dateSelector.computeDate();

      this.filter();
    });
  }

  refreshOffers(): void {
    this.offerLoading = true;

    const params: any = {};

    if (this.filters.product?.id) {
      params.productId = this.filters.product.id;
    }

    this.offerService.findAll(params).subscribe(offers => {
      this.offers = offers;
      this.offerLoading = false;
    });
  }

  refreshPartners(): void {
    this.partnerLoading = true;

    const params: any = {
      sort: 'companyName',
      order: 'asc'
    };

    if (this.filters.accountType) {
      params.accountType = this.filters.accountType;
    }

    if (this.filters.accountManager?.id) {
      params.accountManagerId = this.filters.accountManager?.id;
    }

    if (this.isMediaBuy() && this.filters.advertisingPlatform) {
      params.advertisingPlatform = this.filters.advertisingPlatform;
    }

    this.partnerService.findAll(params).subscribe(partners => {
      this.partners = partners;
      this.partnerLoading = false;
    });
  }

  refreshLandingPages(): void {
    this.landingPageLoading = true;

    const params: any = {
      sort: 'name',
      order: 'asc'
    };

    if (this.filters.offer?.id) {
      params.offerId = this.filters.offer?.id;
    }

    if (this.filters.landingPageActivity) {
      params.activeSince = this.filters.landingPageActivity;
    }

    this.landingPageService.findAll(params).subscribe(landingPages => {
      this.filters.landingPage = null;
      this.landingPages = landingPages;
      this.landingPageLoading = false;
    });
  }

  isMediaBuy(): boolean {
    return this.filters.accountType === 'mediabuy';
  }

  changeAccountManager(): void {
    this.refreshPartners();
  }

  changeAccountTypeManager(): void {
    this.refreshPartners();
  }

  changeAdvertisingPlatform(): void {
    this.refreshPartners();
  }

  changeOffer(): void {
    this.refreshLandingPages();
  }

  changeProduct(): void {
    this.refreshOffers();
  }

  getLandingPagePlaceholder(): string {
    const active = this.filters.landingPageActivity ? ' active' : '';

    return this.landingPages.length ? ('All ' + this.landingPages.length + active + ' promotion link(s)') : 'No' + active + ' promotion link';
  }

  getPoolPlaceholder(): string {
    return this.pools.length ? ('All ' + this.pools.length + ' pool(s)') : 'No pool';
  }

  getPartnerPlaceholder(): string {
    return this.partners.length ? ('All ' + this.partners.length + ' partner(s)') : 'No partner';
  }

  getOfferPlaceholder(): string {
    return this.offers.length ? ('All ' + this.offers.length + ' offer(s)') : 'No offer';
  }

  getProductPlaceholder(): string {
    return this.products.length ? ('All ' + this.products.length + ' product(s)') : 'No product';
  }

  getGroupByColumnName(): string {
    switch (this.currentFilters.groupBy) {
      case 'landingPage':
        return 'Promotion links';
      case 'pool':
        return 'Pools';
      case 'partner':
        return 'Partners';
      case 'offer':
        return 'Offers';
      case 'subid':
        return 'SubID';
      case 'tc1':
        return 'Tracker 1';
      case 'tc2':
        return 'Tracker 2';
      case 'tc3':
        return 'Tracker 3';
      case 'tc4':
        return 'Tracker 4';
      case 'country':
        return 'Countries';
      case 'lang':
        return 'Languages';
    }

    return 'Date';
  }

  getLabel(row: any): string {
    if (this.currentFilters.groupBy.startsWith('date-')) {
      return new Date(row.label).toLocaleDateString();
    }

    if (this.currentFilters.groupBy == 'country') {
      const countryName = this.countryService.getName(row.label);
      return countryName ? countryName : 'Unknown';
    }

    if (this.currentFilters.groupBy == 'lang') {
      const lang = this.langService.getName(row.label);
      return lang ? lang : 'Unknown';
    }

    return row.label;
  }

  getTimeValue(row: any, n: number): string {
    let day: any = row.days[n] ?? null;

    if (this.currentFilters.dataType === 'revenue') {
      if (day && day.revenue) {
        return this.moneyPipe.transform(day.revenue);
      }
      return this.moneyPipe.transform(0);
    }

    if (this.currentFilters.dataType === 'arpu') {
      if (day && day.arpu) {
        return this.moneyPipe.transform(day.arpu);
      }
      return this.moneyPipe.transform(0);
    }

    if (this.currentFilters.dataType === 'roi') {
      if (day && day.roi) {
        return this.percentPipe.transform(day.roi);
      }
      return this.percentPipe.transform(0);
    }

    if (this.currentFilters.dataType === 'saleCount') {
      if (day && day.saleCount) {
        return day.saleCount;
      }
      return '0';
    }

    return '-';
  }

  getHeatmapClass(row: any, n: number): string | null {
    let day: any = row.days[n] ?? null;

    if (this.currentFilters.dataType === 'revenue') {
      if (day && day.cost && day.revenue) {
        const ratio = Math.min(day.revenue / day.cost, 1)
        return 'heatmap-' + Math.min(10, Math.round(Math.ceil(ratio * 10)));
      }
    }

    if (this.currentFilters.dataType === 'arpu') {
      if (day && day.arpu && this.maxArpu > 0) {
        return 'heatmap-' + Math.min(10, Math.round(Math.ceil(day.arpu / this.maxArpu * 10)));
      }
    }

    if (this.currentFilters.dataType === 'roi') {
      if (day && day.roi) {
        return 'heatmap-' + Math.min(10, Math.round(Math.ceil(day.roi * 10)));
      }
    }

    if (this.currentFilters.dataType === 'saleCount') {
      if (day && day.saleCount && this.maxSaleCount) {
        return 'heatmap-' + Math.min(10, Math.round(Math.ceil(day.saleCount / this.maxSaleCount * 10)));
      }
    }

    return null;
  }

  filter(): void {
    this.loading = true;

    this.currentFilters = {
      startDate: this.dateSelector.getDateISO8601From(),
      endDate: this.dateSelector.getDateISO8601To(),
      groupBy: this.filters.groupBy
    };

    if (this.filters.product?.id) {
      this.currentFilters.productId = this.filters.product?.id;
    }

    if (this.filters.offer?.id) {
      this.currentFilters.offerId = this.filters.offer?.id;
    }

    if (this.filters.partner?.id) {
      this.currentFilters.partnerId = this.filters.partner?.id;
    }

    if (this.filters.accountManager) {
      this.currentFilters.accountManagerId = this.filters.accountManager?.id;
    }

    if (this.filters.accountType) {
      this.currentFilters.accountType = this.filters.accountType;
    }

    if (this.filters.advertisingPlatform) {
      this.currentFilters.advertisingPlatformId = this.filters.advertisingPlatform?.id;
    }

    if (this.filters.landingPage?.id) {
      this.currentFilters.landingPageId = this.filters.landingPage?.id;
    } else if (this.filters.landingPageActivity) {
      this.currentFilters.landingPageActiveSince = this.filters.landingPageActivity;
    }

    if (this.filters.pool?.id) {
      this.currentFilters.poolId = this.filters.pool?.id;
    }

    if (this.filters.subid) {
      this.currentFilters.subid = this.filters.subid;
    }

    if (this.filters.tc1) {
      this.currentFilters.tc1 = this.filters.tc1;
    }

    if (this.filters.tc2) {
      this.currentFilters.tc2 = this.filters.tc2;
    }

    if (this.filters.tc3) {
      this.currentFilters.tc3 = this.filters.tc3;
    }

    if (this.filters.tc4) {
      this.currentFilters.tc4 = this.filters.tc4;
    }

    if (this.filters.source) {
      this.currentFilters.source = this.filters.source;
    }

    if (this.filters.country) {
      this.currentFilters.country = this.filters.country;
    }

    if (this.filters.lang) {
      this.currentFilters.lang = this.filters.lang;
    }

    if (this.filters.cross) {
      this.currentFilters.cross = this.filters.cross;
    }

    if (this.filters.coreg) {
      this.currentFilters.coreg = this.filters.coreg;
    }

    if (this.filters.goalTypeNames.length < this.goalTypeNameList.length) {
      this.currentFilters.goalTypeNames = this.filters.goalTypeNames.join(',');
    }

    if (this.filters.dataType) {
      this.currentFilters.dataType = this.filters.dataType;
    }

    if (this.filters.timeCutout) {
      this.currentFilters.timeCutout = this.filters.timeCutout;
    }

    this.loader.startLoading();
    this.userActivity.reportFilters(this.currentFilters);
    this.statisticsService.getROI(this.currentFilters).subscribe((data: any) => {
      this.rows = data.rows;
      this.totals = data.totals;
      this.timeDays = data.timeDays;
      this.maxArpu = data.maxArpu;
      this.maxSaleCount = data.maxSaleCount;
      this.loader.stopLoading();
      this.loading = false;
    });
  }

  downloadCsv(): void {
    if (this.loading || !this.rows.length) {
      return;
    }

    const dataTypeLabel = this.dataTypeList.find(d => d.key === this.currentFilters.dataType)?.label
      ?? this.currentFilters.dataType;

    const headers = [
      this.getGroupByColumnName(),
      'Leads',
      'Acquisition Leads',
      'Reactivation',
      'A/R',
      'Cost',
      'Revenue',
      'CPL',
      ...this.timeDays.map(day => `${dataTypeLabel} J${day}`)
    ];

    const csvRows: string[] = [headers.map(h => this.csvCell(h)).join(',')];

    for (const row of this.rows) {
      csvRows.push([
        this.csvCell(this.getLabel(row)),
        row.leadTotal ?? 0,
        row.leadOk ?? 0,
        row.leadReactivated ?? 0,
        row.leadAR ?? 0,
        this.formatMoney(row.cost),
        this.formatMoney(row.revenue),
        this.formatMoney(row.leadCPL),
        ...this.timeDays.map((_day, i) => this.getTimeRawValue(row, i))
      ].join(','));
    }

    csvRows.push([
      this.csvCell('Total'),
      this.totals.leadTotal ?? 0,
      this.totals.leadOk ?? 0,
      this.totals.leadReactivated ?? 0,
      this.totals.leadAR ?? 0,
      this.formatMoney(this.totals.cost),
      this.formatMoney(this.totals.revenue),
      this.formatMoney(this.totals.leadCPL),
      ...this.timeDays.map(() => '')
    ].join(','));

    this.triggerCsvDownload(csvRows.join('\n'), 'roi-statistics.csv');
  }

  private getTimeRawValue(row: any, i: number): string {
    const day: any = row.days[i] ?? null;

    switch (this.currentFilters.dataType) {
      case 'revenue':
        return this.formatMoney(day?.revenue);
      case 'arpu':
        return this.formatMoney(day?.arpu);
      case 'roi':
        return this.formatPercent(day?.roi);
      case 'saleCount':
        return String(day?.saleCount ?? 0);
    }

    return '';
  }

  private formatMoney(value: any): string {
    const input = parseFloat(value);
    return isNaN(input) ? '0.00' : (input / 100).toFixed(2);
  }

  private formatPercent(value: any): string {
    const input = parseFloat(value);
    return isNaN(input) ? '0.00' : (input * 100).toFixed(2);
  }

  private csvCell(value: any): string {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  }

  private triggerCsvDownload(content: string, filename: string): void {
    this.fileDownload.saveCsv(content, filename);
  }
}
